import React, { useEffect, useState } from 'react'
import { Breadcrumb, Breadcrumbs, Card as CoreCard, Collapse, Icon, Tag } from '@blueprintjs/core'
import { Button, Col, Dialog, Divider, H5, Row } from '../../Elements'
import { useNavigate } from 'react-router-dom'
import { primaryTheme } from '../../utils/helpers'

export const Card = ({ children, style, ...props }) => {
    return (
        <CoreCard style={{ margin: '10px 0px 10px 0px', maxWidth: '97vw', ...style }} {...props} >
            {children}
        </CoreCard>
    )
}

const CollapsableCardContent = ({
    title,
    icon,
    iconProps,
    defaultAsClosed = false,
    showDivider = false,
    topLevelOpen = null,
    children,
    titleStyles = {}
}) => {
    const [isOpen, setIsOpen] = useState(!defaultAsClosed)

    useEffect(() => {
        if (topLevelOpen !== null) {
            setIsOpen(topLevelOpen)
        }
    }, [topLevelOpen])

    return <>
        <div
            onClick={() => setIsOpen(!isOpen)}
            style={{ cursor: 'pointer' }}
        >
            <Icon
                iconSize={18}
                icon={isOpen ? 'caret-down' : 'caret-right'}
            />
            {title && <H5 style={{ display: 'inline', color: primaryTheme, ...titleStyles }}>{title}</H5>}
            {icon && <Icon icon={icon} iconSize={18} style={{ float: 'right' }} {...iconProps} />}
            {(isOpen && showDivider) && <Divider />}
        </div>
        <Collapse isOpen={isOpen} children={children} />
    </>
}

export const CollapsableCard = ({
    title,
    defaultAsClosed,
    icon,
    iconProps,
    topLevelOpen,
    showDivider = false,
    children,
    style,
    titleStyles = {},
    ...props
}) => {
    return (
        <Card style={{ margin: '10px 0px 10px 0px', maxWidth: '97vw', ...style }} {...props} >
            <CollapsableCardContent
                title={title}
                defaultAsClosed={defaultAsClosed}
                icon={icon}
                iconProps={iconProps}
                topLevelOpen={topLevelOpen}
                showDivider={showDivider}
                titleStyles={titleStyles}
            >
                {children}
            </CollapsableCardContent>
        </Card>
    )
}

export const CollapsableHeader = ({
    title,
    subtitle,
    children,
    defaultAsClosed = false,
    headerClassName,
    titleClassName,
    topLevelOpen = null,
    childLength,
    indicator = null,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(!defaultAsClosed)

    useEffect(() => {
        if (topLevelOpen !== null) {
            setIsOpen(topLevelOpen)
        }
    }, [topLevelOpen])

    return (
        <>
            <div
                className={headerClassName}
                style={{
                    cursor: 'pointer',
                    background: primaryTheme,
                    ...props.style
                }}
                onClick={() => {
                    setIsOpen(!isOpen)
                }}
            >
                <span className={titleClassName} style={{
                    flexBasis: '100%',
                    position: 'relative',
                    background: primaryTheme,
                }}>
                    <div>
                        <Icon
                            iconSize={14}
                            icon={isOpen ? 'caret-down' : 'caret-right'}
                        />
                        <span>
                            {title}<br />{subtitle}
                        </span>
                    </div>
                    <span style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                    }}>
                        {childLength !== undefined &&
                            <Tag large round>
                                {childLength}
                            </Tag>
                        }
                        {indicator}
                    </span>
                </span>
            </div>
            <Collapse isOpen={isOpen} children={children} />
        </>
    )
}

const defaultBreadcrumbCardStyling = {
    margin: '10px 0px 10px 0px',
    maxWdith: '97vw',
    top: '49px',
    zIndex: 10,
}


const defaultStickyBreadcrumbCardStyling = {
    margin: '10px 0px 10px 0px',
    maxWdith: '97vw',
    top: '49px',
    zIndex: 10,
    position: 'sticky'
}


export const CardWithBreadcrumb = ({
    children,
    items,
    isMobile,
    expandTitle = false,
    sticky = true,
    style = {},
    ...props
}) => {
    const navigate = useNavigate()
    const renderBreadcrumb = ({ text, to, icon, isLoading }) => {
        return (
            <Breadcrumb
                disabled={!to}
                onClick={() => navigate(to)}
                icon={icon}
                className={isLoading ? 'bp3-skeleton' : isMobile ? 'mobile-breadcrumb' : ''}
            >
                {text}
            </Breadcrumb>
        )
    }

    if (isMobile) style = { ...style, padding: '10px' }

    return <Card style={{ ...(sticky ? defaultStickyBreadcrumbCardStyling : defaultBreadcrumbCardStyling), ...style }}
        elevation={3} {...props}>
        <div style={{ display: 'inline-block', maxWidth: expandTitle ? '100%' : '57%' }}>
            <Breadcrumbs
                items={items}
                minVisibleItems={2}
                breadcrumbRenderer={renderBreadcrumb}
            />
        </div>

        <div style={{ float: 'right' }}>
            {children}
        </div>
    </Card>
}

export const MobileListCard = ({
    dialogTitle,
    readOnlyContent,
    editViewContent,
    autoOpen,
    onRemove,
    uneditable,
    unremovable,
    idx
}) => {
    const [popoverState, setPopoverState] = useState(autoOpen)
    const closeAndRemove = () => {
        setPopoverState(false)
        onRemove()
    }
    return <Card style={{ paddingTop: '.5em', paddingBottom: '.5em' }} className='mobile-list-read-only-card'
        key={`mobile-list-card-${idx || 'undefined-idx'}`}>
        <Row>
            <Col xs={6}>
                {!uneditable && <Icon icon='edit' color={primaryTheme} iconSize='15' onClick={() => setPopoverState(true)} />}
            </Col>
            <Col xs={6} style={{ textAlign: 'right' }}>
                {!unremovable && <Icon icon='delete' iconSize='15' intent='danger' onClick={onRemove} />}
            </Col>
        </Row>

        {(!uneditable || !unremovable) && <Divider />}

        <Dialog
            title={dialogTitle || 'Edit'}
            isOpen={popoverState}
            onClose={() => setPopoverState(false)}
        >
            <div style={{ padding: '.7em' }}>
                {editViewContent}
            </div>

            <Row style={{ padding: '0 5vw' }}>
                <Col xs={6}>
                    <Button icon='remove' intent='danger' text='Remove' onClick={closeAndRemove} style={{ margin: 0 }} />
                </Col>
                <Col xs={6} style={{ textAlign: 'right' }}>
                    <Button icon='tick' intent='primary' text='Done' onClick={() => setPopoverState(false)} />
                </Col>
            </Row>
        </Dialog>

        {readOnlyContent}
    </Card>
}

export const AddCard = ({ onClick, children, ...props }) => {
    return (
        <Card
            className='add-card'
            onClick={onClick || (() => true)}
            {...props}
        >
            <H5 style={{ display: 'inline', color: 'white' }}>
                <Icon icon='add' style={{ color: 'white', paddingRight: '1em' }} />
                Add
            </H5>
            {children}
        </Card>
    )
}